/**
 * Soft≠Live Page177 base + Page222 dual residual (list_plane ∥ summary_plane).
 * Mount: /ops/alarms (panel tab)
 * Buses:
 *   GET /ops/alarms/status (always-on Live posture · dual list ∥ summary)
 *   GET /alarms when list_plane_allowed
 *   GET /alarms/summary + GET /activity/signals when summary_plane_allowed
 *   GET /devices?limit= labels when either plane opens (bounded)
 *   POST /devices/{id}/alert/off when silence (list plane · BE scope)
 * Soft residual closed (operator DOM never Soft-hides empty History):
 *  - dual-path without posture SoT
 *  - empty history hide + engineer empty narrative in operator copy
 *  - acknowledge/close theatre (store has no closed state)
 *  - summary brochure KPI invent when summary plane sealed
 *  - unbounded devices label pull (staff fleet thrash)
 *  - raw API path / error code paint in operator DOM
 */
import { useMemo, useState } from "react";
import { html, Ico, Alert, go } from "./ui.js";
import { api, path, useApi } from "./api.js";
import { confirmDeviceCommand } from "./device-command-gate.js";
import { formatUserInstant } from "./time.js";
import { boardLabelSay } from "./operator-say.js";

const WINDOWS = [
  { id: 6, label: "6h" },
  { id: 24, label: "24h" },
  { id: 72, label: "72h" },
  { id: 168, label: "7d" },
];

const OPEN_WINDOW_S = 15 * 60;

const ALERT_TABS = [
  { id: "events", label: "Events", long: "Events" },
  { id: "tops", label: "Top", long: "Top sources" },
  { id: "sigs", label: "Sigs", long: "Signals" },
];

/** Soft≠Live: missing contract ops must not crash the desk. */
function safePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__ops_alarms_status") return "/ops/alarms/status";
    if (op === "get__alarms") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q).map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length ? `/alarms?${parts.join("&")}` : "/alarms";
    }
    if (op === "get__alarms_summary") return "/alarms/summary";
    if (op === "get__activity_signals") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q).map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length ? `/activity/signals?${parts.join("&")}` : "/activity/signals";
    }
    if (op === "get__devices") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q).map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length ? `/devices?${parts.join("&")}` : "/devices";
    }
    if (op === "post__devices_p_alert_off") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? `/devices/${encodeURIComponent(id)}/alert/off` : "";
    }
    return "";
  }
}

/** Operator-facing bus / act errors — never paths or stacks. */
export function softAlarmsErrorOf(err) {
  const n = String(err == null ? "" : err).trim().toLowerCase();
  if (!n) return "That alerts action could not be completed.";
  if (/\b403\b|forbidden|insufficient|not in your scope|can_send|scope/.test(n)) {
    return "Your account cannot do that with alerts. Ask a site manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(n)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found/.test(n)) {
    return "That device or alert row is not available.";
  }
  if (/\b429\b|too many/.test(n)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|product_off/.test(n)) {
    return "Alerts are not armed on this site right now.";
  }
  if (/network|failed to fetch|timeout|aborted/.test(n)) {
    return "Network issue — try Refresh.";
  }
  if (/\/ops\/|\/alarms|\/devices|\/activity|http|get |post |status_path|sql|traceback/.test(n)) {
    return "That alerts action could not be completed.";
  }
  return "That alerts action could not be completed.";
}

export function softAlarmsSeatLabelOf(role) {
  const r = String(role || "").toLowerCase();
  if (r === "user") return "Operator";
  if (r === "admin") return "Site manager";
  if (r === "superadmin") return "Platform";
  if (r === "sa_staff") return "Staff";
  return role || "Seat";
}

/** Pure: dual seat from Live Ops Alarms posture (Page222). */
export function opsAlarmsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const alarmMax = Number(lim.alarm_paint_max);
  const signalMax = Number(lim.signal_paint_max);
  const silenceMax = Number(lim.silence_target_max);
  const regMax = Number(lim.register_label_max);
  const listAllowed = (p.list_plane_allowed === true || p.list_allowed === true);
  const summaryAllowed = (p.summary_plane_allowed === true || p.summary_allowed === true);
  return {
    listAllowed,
    summaryAllowed,
    signalsAllowed: (p.signals_allowed === true),
    registerAllowed: (p.register_read_allowed === true),
    silenceAllowed: (p.silence_allowed === true),
    reReadAllowed: (p.re_read_allowed === true),
    acknowledgeAllowed: (p.acknowledge_allowed === true),
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    alarmPaintMax:
      Number.isFinite(alarmMax) && alarmMax > 0 ? Math.floor(alarmMax) : 100,
    signalPaintMax:
      Number.isFinite(signalMax) && signalMax > 0 ? Math.floor(signalMax) : 40,
    silenceTargetMax:
      Number.isFinite(silenceMax) && silenceMax > 0 ? Math.floor(silenceMax) : 40,
    registerLabelMax:
      Number.isFinite(regMax) && regMax > 0 ? Math.floor(regMax) : 128,
  };
}

/** Pure: flat device id→label from GET /devices. */
export function alarmsDevicesOf(pack) {
  const items = (pack && (pack.items || pack.devices)) || [];
  const rows = Array.isArray(items) ? items : [];
  const mapped = rows
    .map((r) => {
      if (!r) return null;
      const id = String(r.device_id || r.id || "").trim();
      if (!id) return null;
      const kind = String(r.kind || r.device_kind || "").toLowerCase();
      return { id, label: boardLabelSay(r, id), kind };
    })
    .filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const d of mapped) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    out.push(d);
  }
  return out;
}

function clockOf(iso) {
  return iso ? formatUserInstant(iso, { date: false }) : "—";
}

function secondsSince(iso) {
  if (!iso) return Infinity;
  const raw = String(iso);
  const d = new Date(
    raw.endsWith("Z") || /[+-]\d\d:\d\d$/.test(raw) ? raw : raw + "Z",
  );
  return Number.isNaN(d.getTime()) ? Infinity : (Date.now() - d.getTime()) / 1000;
}

/** Pure: map Live /alarms items. */
export function alarmRowsOf(pack) {
  const rows = (pack && (pack.items || pack.alarms || pack.rows)) || [];
  if (!Array.isArray(rows)) return [];
  return rows.map((a, i) => {
    const created = a.created_at || a.ts || a.at || "";
    const dev = String(a.source_id || a.device_id || a.dev || "").trim();
    return {
      id: String(a.id != null ? a.id : ("r" + i)),
      dev,
      zone: String(a.zone || "").trim() || "—",
      by: String(a.triggered_by || a.by || "").trim() || "—",
      ts: clockOf(created),
      at: created,
      open: secondsSince(created) < OPEN_WINDOW_S,
      fanout: Number(a.fanout_count != null ? a.fanout_count : a.fanout || 0),
      notified: Number(a.email_sent || 0) > 0 || !!a.notified,
      emailDetail: String(a.email_detail || "").trim(),
    };
  });
}

/** Pure: summary KPI shape. */
export function alarmSummaryOf(pack) {
  const p = pack && typeof pack === "object" ? pack : {};
  const tops = p.top_sources_7d || p.top_sources || [];
  return {
    last24: Number(p.last_24h != null ? p.last_24h : 0),
    last7d: Number(p.last_7d != null ? p.last_7d : 0),
    tops: Array.isArray(tops)
      ? tops.map((t) => ({
        id: String(t.source_id || t.device_id || t.id || "").trim(),
        n: Number(t.c != null ? t.c : t.count != null ? t.count : 0),
      })).filter((t) => t.id)
      : [],
  };
}

/** Pure: activity signal rows (Live feed shape + legacy). */
export function signalRowsOf(pack, paintMax) {
  const rows = (pack && (pack.items || pack.signals)) || [];
  if (!Array.isArray(rows)) return [];
  const max = Number.isFinite(Number(paintMax)) && Number(paintMax) > 0
    ? Math.floor(Number(paintMax))
    : 12;
  return rows.slice(0, max).map((s, i) => {
    const kind = String(s.kind || s.event_type || s.signal || s.what || "signal").trim();
    const who = String(s.who || s.actor || s.triggered_by || "").trim();
    const detailRaw = String(
      s.detail || s.summary || s.email_detail || s.detail_json || "",
    ).trim();
    const zone = String(s.zone || "").trim();
    const bits = [];
    if (who) bits.push(who);
    if (zone) bits.push(zone);
    if (detailRaw) bits.push(detailRaw.slice(0, 80));
    return {
      id: String(s.id != null ? s.id : (s._row != null ? s._row : ("s" + i))),
      kind,
      dev: String(s.device_id || s.source_id || "").trim(),
      ts: clockOf(s.created_at || s.ts || ""),
      detail: bits.join(" · ").slice(0, 120) || "—",
    };
  });
}

export function OpsAlarmsDesk({ onSelect, toast }) {
  const posture = useApi(safePath("get__ops_alarms_status"));
  const post = posture.data || {};
  const seat = opsAlarmsSeatOf(
    posture.loading || !posture.data
      ? {
          enabled: true,
          list_plane_allowed: false,
          summary_plane_allowed: false,
          list_allowed: false,
          summary_allowed: false,
          signals_allowed: false,
          register_read_allowed: false,
          silence_allowed: false,
          re_read_allowed: false,
          limits: {
            alarm_paint_max: 100,
            signal_paint_max: 40,
            silence_target_max: 40,
            register_label_max: 128,
          },
        }
      : post,
  );
  const [hours, setHours] = useState(24);
  const [silencing, setSilencing] = useState(false);
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [tab, setTab] = useState("events");

  const list = useApi(
    seat.listAllowed
      ? safePath("get__alarms", {
        query: { since_hours: hours, limit: seat.alarmPaintMax },
      })
      : "",
  );
  const summary = useApi(seat.summaryAllowed ? safePath("get__alarms_summary") : "");
  const signals = useApi(
    seat.signalsAllowed
      ? safePath("get__activity_signals", {
        query: { since_hours: hours, limit: seat.signalPaintMax },
      })
      : "",
  );
  const devicesR = useApi(
    seat.registerAllowed
      ? safePath("get__devices", {
        // Soft≠Live: always bound (sa_staff custody ignores limit without honor clamp).
        query: { limit: seat.registerLabelMax, skip_cache: 1 },
      })
      : "",
  );

  const devices = useMemo(() => alarmsDevicesOf(devicesR.data), [devicesR.data]);
  const rows = useMemo(() => {
    const all = alarmRowsOf(list.data);
    return all.slice(0, seat.alarmPaintMax);
  }, [list.data, seat.alarmPaintMax]);
  const sum = useMemo(
    () => (seat.summaryAllowed ? alarmSummaryOf(summary.data) : alarmSummaryOf(null)),
    [summary.data, seat.summaryAllowed],
  );
  const sigs = useMemo(
    () => (seat.signalsAllowed
      ? signalRowsOf(signals.data, Math.min(12, seat.signalPaintMax))
      : []),
    [signals.data, seat.signalsAllowed, seat.signalPaintMax],
  );
  const openN = rows.filter((r) => r.open).length;
  const view = onlyOpen ? rows.filter((r) => r.open) : rows;
  const bothSealed = !seat.listAllowed && !seat.summaryAllowed;
  const deviceLabel = (id) => {
    const d = devices.find((x) => x.id === id);
    return (d && d.label) || id || "—";
  };

  const reload = () => {
    if (!seat.reReadAllowed && !posture.loading) {
      toast && toast("Refresh is closed for this seat", "warn");
      return;
    }
    posture.reload && posture.reload();
    list.reload && list.reload();
    summary.reload && summary.reload();
    signals.reload && signals.reload();
    devicesR.reload && devicesR.reload();
    toast && toast("Alerts refreshed", "go");
  };

  const silenceAll = async () => {
    if (silencing || !seat.silenceAllowed) {
      if (!seat.silenceAllowed) {
        toast && toast("Silence is closed for this seat", "warn");
      }
      return;
    }
    const fromHist = rows.map((a) => a.dev).filter(Boolean);
    const sirens = devices.filter((d) => d.kind === "siren").map((d) => d.id);
    const uniq = [...new Set(
      fromHist.length ? fromHist : sirens,
    )].slice(0, seat.silenceTargetMax);
    if (!uniq.length) {
      toast && toast("No sirens to silence in this window", "warn");
      return;
    }
    const targets = uniq.map((id) => {
      const d = devices.find((x) => x.id === id);
      const row = rows.find((a) => a.dev === id);
      return { id, label: (d && d.label) || (row && row.label) || id };
    });
    const approved = await confirmDeviceCommand({
      kind: "bulk_silence",
      targets,
      detail: "Stops sirens on the listed machines in this Alerts window. Cancel leaves them unchanged.",
      confirmLabel: "Silence listed sirens",
    });
    if (!approved) return;
    setSilencing(true);
    let ok = 0;
    let fail = 0;
    let lastFail = "";
    try {
      for (const id of uniq) {
        try {
          await api(safePath("post__devices_p_alert_off", { pathParams: [id] }), {
            method: "POST",
          });
          ok += 1;
        } catch (e) {
          fail += 1;
          lastFail = softAlarmsErrorOf(e && (e.message || e.detail || e));
        }
      }
      if (fail === 0) {
        toast && toast(ok + " silence command" + (ok === 1 ? "" : "s") + " sent", "go");
      } else {
        toast
          && toast(
            ok
              ? (ok + " sent · " + fail + " refused")
              : (lastFail || "Silence refused"),
            fail === uniq.length ? "bad" : "warn",
          );
      }
    } finally {
      setSilencing(false);
    }
  };

  const denied = !!(list.error && /\b40[13]\b|forbidden|insufficient/i.test(list.error || ""));
  const sumDenied = !!(summary.error && /\b40[13]\b|forbidden|insufficient/i.test(summary.error || ""));
  const roleLabel = softAlarmsSeatLabelOf(seat.role || post.principal_role || "");
  const pulseLine = posture.loading
    ? "Checking access…"
    : seat.listAllowed
      ? (list.loading && !list.data
        ? "Loading events…"
        : openN
          ? `${openN} open · last ${Math.round(OPEN_WINDOW_S / 60)} min`
          : `Quiet · last ${hours}h`)
      : seat.summaryAllowed
        ? "Event list closed · summary still open"
        : "Alerts closed for this seat";

  if (!posture.loading && (post.enabled === false || bothSealed)) {
    return html`<div
      className="ops-hud ops-alerts ops-field ops-field--alerts ops-alarms-desk ops-alarms-desk--liquid"
      data-cmc-page="page120-alarms" data-cmc-alarms="page53"
      data-cmc-residual="page222-alarms"
      data-field="v555">
      <header className="ops-hud__top">
        <div className="ops-hud__brand">
          <span className="ops-hud__chip">Alerts</span>
          <span className="ops-hud__chip ops-hud__chip--live">Closed</span>
        </div>
        <button type="button" className="ops-hud__refresh"
          disabled=${!seat.reReadAllowed || posture.loading}
          onClick=${reload} title="Refresh">
          <${Ico} d="refresh" w=${13}/>
        </button>
      </header>
      <p className="ops-hud__pulse">Seat closed</p>
      <p className="ops-hud__quiet">No alerts for ${roleLabel || "this seat"}.</p>
    </div>`;
  }

  return html`<div
    className="ops-hud ops-alerts ops-field ops-field--alerts ops-alarms-desk ops-alarms-desk--liquid"
    data-cmc-page="page120-alarms" data-cmc-alarms="page53"
    data-cmc-residual="page222-alarms"
    data-field="v555">

    <div className="ops-hud__scan" aria-hidden="true" />

    <header className="ops-hud__top">
      <div className="ops-hud__brand">
        <span className="ops-hud__chip">Alerts</span>
        <span className="ops-hud__chip">${seat.listAllowed ? (openN || "0") : "—"}</span>
      </div>
      <div className="ops-alerts__head-acts">
        <button type="button" className="ops-hud__refresh"
          disabled=${!seat.reReadAllowed && !posture.loading}
          onClick=${reload} title="Refresh">
          <${Ico} d="refresh" w=${13}/>
        </button>
        <button type="button" className="btn btn--ghost btn--xs ops-alerts__silence"
          disabled=${silencing || !seat.silenceAllowed}
          title="Stop sirens on devices in this window"
          onClick=${silenceAll}>
          ${silencing ? "…" : "Silence sirens"}
        </button>
      </div>
    </header>

    <p className="ops-hud__pulse" title="Alerts status">${pulseLine}</p>
    <span className="sr-only">alerts desk</span>

    <div className="ops-hud__meters ops-alerts__meters" role="group" aria-label="Alarm KPIs">
      <div className="ops-hud__meter" data-on=${seat.listAllowed && openN ? "1" : "0"} data-tone="warn">
        <b>${!seat.listAllowed
          ? "—"
          : (list.loading && !list.data ? "…" : openN)}</b>
        <em>Open 15m</em>
        <s className="ops-hud__meter-bar" style=${{ transform: `scaleX(${openN ? 1 : 0.18})` }}/>
      </div>
      <div className="ops-hud__meter" data-on=${seat.listAllowed && rows.length ? "1" : "0"} data-tone="go">
        <b>${!seat.listAllowed
          ? "—"
          : (list.loading && !list.data ? "…" : rows.length)}</b>
        <em>Window</em>
        <s className="ops-hud__meter-bar" style=${{
          transform: `scaleX(${rows.length ? Math.min(1, Math.max(0.18, rows.length / 12)) : 0.18})`,
        }}/>
      </div>
      <div className="ops-hud__meter" data-on=${seat.summaryAllowed && sum.last24 ? "1" : "0"} data-tone="go">
        <b>${!seat.summaryAllowed
          ? "—"
          : (sumDenied ? "—" : (summary.loading && !summary.data ? "…" : sum.last24))}</b>
        <em>24h</em>
        <s className="ops-hud__meter-bar" style=${{
          transform: `scaleX(${sum.last24 ? Math.min(1, Math.max(0.18, sum.last24 / 20)) : 0.18})`,
        }}/>
      </div>
      <div className="ops-hud__meter" data-on=${seat.summaryAllowed && sum.last7d ? "1" : "0"} data-tone="warn">
        <b>${!seat.summaryAllowed
          ? "—"
          : (sumDenied ? "—" : (summary.loading && !summary.data ? "…" : sum.last7d))}</b>
        <em>7d</em>
        <s className="ops-hud__meter-bar" style=${{
          transform: `scaleX(${sum.last7d ? Math.min(1, Math.max(0.18, sum.last7d / 40)) : 0.18})`,
        }}/>
      </div>
    </div>

    <div className="ops-alerts__tools">
      <div className="ops-seg ops-seg--warm ops-alerts__windows" role="toolbar" aria-label="Alarm window">
        ${WINDOWS.map((w) => html`
          <button key=${w.id} type="button" className="ops-seg__btn ops-alerts__win"
            data-on=${hours === w.id ? "1" : "0"}
            disabled=${!seat.listAllowed && !seat.summaryAllowed}
            title=${w.label + " window"}
            onClick=${() => setHours(w.id)}>${w.label}</button>`)}
        <button type="button" className="ops-seg__btn ops-seg__btn--latch ops-alerts__win"
          data-on=${onlyOpen ? "1" : "0"}
          disabled=${!seat.listAllowed}
          title=${!seat.listAllowed ? "List closed" : "Show only open window"}
          onClick=${() => seat.listAllowed && setOnlyOpen((v) => !v)}>
          Open
        </button>
      </div>
      <div className="ops-seg ops-alerts__tabs" role="tablist" aria-label="Alert panes">
        ${ALERT_TABS.map((t) => {
          const sealed =
            (t.id === "tops" && !seat.summaryAllowed)
            || (t.id === "sigs" && !seat.signalsAllowed)
            || (t.id === "events" && !seat.listAllowed);
          return html`
          <button key=${t.id} type="button" role="tab" className="ops-seg__btn ops-alerts__tab"
            data-on=${tab === t.id ? "1" : "0"}
            data-dim=${sealed ? "1" : "0"}
            disabled=${sealed}
            title=${sealed ? ((t.long || t.label) + " · sealed for this seat") : (t.long || t.label)}
            aria-label=${t.long || t.label}
            onClick=${() => !sealed && setTab(t.id)}>${t.label}</button>`;
        })}
      </div>
    </div>

    ${denied
      ? html`<${Alert} tone="bad" title="Events closed" say=${softAlarmsErrorOf(list.error)}/>`
      : null}
    ${list.error && !denied
      ? html`<${Alert} tone="warn" title="Events unavailable" say=${softAlarmsErrorOf(list.error)}/>`
      : null}

    <section className="ops-alerts__stage" aria-label="Alert stage">
      ${tab === "events" ? html`
        <div className="ops-hud__lane-h">
          <p className="ops-hud__k"><s></s>Events · ${hours}h</p>
          <em className="ops-alerts__match">
            ${seat.listAllowed ? (view.length + " shown") : "closed"}
          </em>
        </div>
        ${!seat.listAllowed
          ? html`<div className="ops-hud__quiet"><b>Event list closed</b> · Not available for this seat.</div>`
          : list.loading && !list.data
            ? html`<p className="ops-hud__quiet">Loading…</p>`
            : !view.length
              ? html`<div className="ops-hud__quiet">
                  <b>Nothing in this window</b> · No alarms reported for your devices.
                </div>`
              : html`<div className="ops-alerts__list" role="list">
                  ${view.map((a) => html`
                    <button key=${a.id} type="button" className="ops-alerts__row" role="listitem"
                      data-state=${a.open ? "open" : "quiet"}
                      onClick=${() => a.dev && onSelect && onSelect(a.dev)}>
                      <span className="ops-alerts__dot" aria-hidden="true"/>
                      <span className="ops-alerts__main">
                        <b>${deviceLabel(a.dev)}</b>
                        <u>
                          ${a.by !== "—" ? a.by + " · " : ""}${a.zone}
                          ${a.notified ? " · notified" : ""}
                          ${a.fanout ? " · relay " + a.fanout : ""}
                        </u>
                      </span>
                      <em className="ops-alerts__trail" data-state=${a.open ? "open" : "quiet"}>
                        ${a.open ? "OPEN" : a.ts}
                      </em>
                    </button>`)}
                </div>`}
        <p className="ops-alerts__note">Silence stops loud sirens · event history stays for audit.</p>
      ` : null}

      ${tab === "tops" ? html`
        <div className="ops-hud__lane-h">
          <p className="ops-hud__k"><s></s>Top sources · 7d</p>
          <em className="ops-alerts__match">counts</em>
        </div>
        ${!seat.summaryAllowed
          ? html`<div className="ops-hud__quiet"><b>Summary closed</b> · Not available for this seat.</div>`
          : sumDenied
            ? html`<${Alert} tone="warn" title="Summary closed" say=${softAlarmsErrorOf(summary.error)}/>`
            : !sum.tops.length
              ? html`<p className="ops-hud__quiet">No top sources in the last 7 days.</p>`
              : html`<div className="ops-alerts__list" role="list">
                  ${sum.tops.map((t) => html`
                    <button key=${t.id} type="button" className="ops-alerts__row" role="listitem"
                      onClick=${() => onSelect && onSelect(t.id)}>
                      <span className="ops-alerts__dot" data-tone="sum" aria-hidden="true"/>
                      <span className="ops-alerts__main">
                        <b>${deviceLabel(t.id)}</b>
                        <u>${t.n} alert${t.n === 1 ? "" : "s"}</u>
                      </span>
                      <em className="ops-alerts__count">${t.n}</em>
                    </button>`)}
                </div>`}
      ` : null}

      ${tab === "sigs" ? html`
        <div className="ops-hud__lane-h">
          <p className="ops-hud__k"><s></s>Signals</p>
          <em className="ops-alerts__match">${hours}h</em>
        </div>
        ${!seat.signalsAllowed
          ? html`<p className="ops-hud__quiet">Signals closed for this seat.</p>`
          : signals.error
            ? html`<${Alert} tone="warn" title="Signals unavailable" say=${softAlarmsErrorOf(signals.error)}/>`
            : !sigs.length
              ? html`<p className="ops-hud__quiet">No activity signals in this window.</p>`
              : html`<div className="ops-alerts__list" role="list">
                  ${sigs.map((x) => html`
                    <div key=${x.id} className="ops-alerts__row ops-alerts__row--static" role="listitem">
                      <span className="ops-alerts__dot" data-tone="sig" aria-hidden="true"/>
                      <span className="ops-alerts__main">
                        <b>${x.kind}${x.dev ? " · " + deviceLabel(x.dev) : ""}</b>
                        <u>${x.detail || "—"}</u>
                      </span>
                      <em className="ops-alerts__trail">${x.ts}</em>
                    </div>`)}
                </div>`}
      ` : null}
    </section>

    <footer className="ops-hud__foot ops-alerts__foot">
      <span className="ops-hud__foot-role" data-soft=${seat.listAllowed ? "0" : "1"}>
        ${roleLabel || "Alerts"}
      </span>
      <button type="button" className="btn btn--ghost btn--xs" onClick=${() => go("/settings/alarm")}>Alarm policy</button>
    </footer>
  </div>`;
}
